import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { RegisterResponse } from '../../models/registerResponse';
import { ForgotPasswordResponse } from '../../models/forgotPasswordResponse';
import { environment } from '../../../../environments/environment';
import { CurrentUserService } from '../../../shared/services/current-user.service';
import { TravelBoardService } from '../../../travel-board/services/travel-board.service';
import { UpdateNoteService } from '../../../update-notes/services/update-note.service';
import { JourneyService } from '../../../shared/services/journey.service';
import { OnboardingService } from '../../../shared/services/onboarding.service';

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    preferredLanguage?: string;
  };
}

interface ResetPasswordResponse {
  success: boolean;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrlLogin = environment.apiUrl + '/auth/login';
  private apiUrlRegister = environment.apiUrl + '/auth/register';
  private apiUrlForgotPasword = environment.apiUrl + '/auth/forgot-password';
  private apiUrlResetPassword = environment.apiUrl + '/auth/reset-password';

  private http = inject(HttpClient);
  private currentUserService = inject(CurrentUserService);
  private travelBoardService = inject(TravelBoardService);
  private updateNoteService = inject(UpdateNoteService);
  private journeyService = inject(JourneyService);
  private onboardingService = inject(OnboardingService);

  login(email: string, password: string): Observable<LoginResponse> {
    const loginData = { email, password };
    return this.http.post<LoginResponse>(this.apiUrlLogin, loginData, {
      withCredentials: true,
    });
  }

  logout() {
    return this.http.get(environment.apiUrl + '/auth/logout').pipe(
      tap(() => {
        // Clear all user-related data on logout
        this.currentUserService.clearCurrentUser();
        this.travelBoardService.clearData();
        this.updateNoteService.reset();
        this.journeyService.reset();
        this.onboardingService.reset();
      }),
    );
  }

  forgotPassword(email: string): Observable<ForgotPasswordResponse> {
    const forgotPassswordData = { email };
    return this.http.post<ForgotPasswordResponse>(
      this.apiUrlForgotPasword,
      forgotPassswordData,
    );
  }
  resetPassword(
    token: string,
    newPassword: string,
  ): Observable<ResetPasswordResponse> {
    return this.http.post<ResetPasswordResponse>(
      this.apiUrlResetPassword,
      { password: newPassword },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    );
  }

  register(registerData: RegisterResponse): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(this.apiUrlRegister, registerData, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  isAuthenticated(): Observable<boolean> {
    return this.http
      .get<boolean>(environment.apiUrl + '/isAuthenticated', {
        withCredentials: true,
      })
      .pipe(
        map((isAuthenticated) => {
          return isAuthenticated;
        }),
        catchError((err) => {
          console.error(err);
          return of(false);
        }),
      );
  }
}
